import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { acceptExpertInvitation } from '../../services/ExpertReviewService';

export default function ExpertInvitation() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    try {
      const result = await acceptExpertInvitation(token, password);
      setMessage(result.detail);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Invitation is invalid or expired.');
    }
  };

  return (
    <main className="flex min-h-screen items-start justify-center bg-slate-950 px-4 py-16 sm:items-center">
      <section className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-700 bg-white shadow-2xl dark:bg-slate-900">
        <header className="bg-gradient-to-br from-blue-800 via-blue-700 to-indigo-700 px-7 py-8">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-200">Expert invitation</p>
          <h1 className="mt-2 text-3xl font-bold text-white">Set up expert account</h1>
          <p className="mt-3 text-sm leading-6 text-blue-100">Create a secure password to activate your review workspace.</p>
        </header>
        <div className="p-7">
          {message ? (
            <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"><p className="text-emerald-800">{message}</p><Link className="mt-3 inline-block font-semibold text-blue-700 underline" to="/login">Sign in</Link></div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
              <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Password
                <span className="relative mt-2 block">
                  <input required minLength={8} type={showPassword ? 'text' : 'password'} className="w-full rounded-xl border border-slate-300 bg-white p-3 pr-12 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} />
                  <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword((visible) => !visible)} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-slate-500 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" aria-hidden="true" /> : <EyeIcon className="h-5 w-5" aria-hidden="true" />}
                  </button>
                </span>
              </label>
              <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Confirm password
                <span className="relative mt-2 block">
                  <input required minLength={8} type={showConfirmPassword ? 'text' : 'password'} className="w-full rounded-xl border border-slate-300 bg-white p-3 pr-12 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
                  <button type="button" aria-label={showConfirmPassword ? 'Hide confirmation password' : 'Show confirmation password'} aria-pressed={showConfirmPassword} onClick={() => setShowConfirmPassword((visible) => !visible)} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-slate-500 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" aria-hidden="true" /> : <EyeIcon className="h-5 w-5" aria-hidden="true" />}
                  </button>
                </span>
              </label>
              <p className="text-xs leading-5 text-slate-500">Use at least 8 characters. You can reveal either field to check what you entered.</p>
              <button className="w-full rounded-xl bg-blue-700 p-3 font-semibold text-white transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">Activate account</button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
